import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ChevronLeft, ChevronRight, X, MapPin, Clock, Users, ArrowRight } from "lucide-react";
import StatusBadge from "@/components/app/StatusBadge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const MONTHS = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre",
];
const DAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

function fmt(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export default function CalendarPage() {
  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [jobs, setJobs] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);

  const { gridStart, gridEnd, monthStart, monthEnd } = useMemo(() => {
    const start = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const end = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
    const dowStart = (start.getDay() + 6) % 7; // Mon=0
    const gStart = new Date(start);
    gStart.setDate(start.getDate() - dowStart);
    const dowEnd = (end.getDay() + 6) % 7;
    const gEnd = new Date(end);
    gEnd.setDate(end.getDate() + (6 - dowEnd));
    return { gridStart: gStart, gridEnd: gEnd, monthStart: start, monthEnd: end };
  }, [cursor]);

  useEffect(() => {
    api
      .get("/jobs", { params: { date_from: fmt(gridStart), date_to: fmt(gridEnd) } })
      .then((r) => setJobs(r.data));
  }, [gridStart, gridEnd]);

  const byDate = useMemo(() => {
    const m = {};
    jobs.forEach((j) => {
      m[j.scheduled_date] = m[j.scheduled_date] || [];
      m[j.scheduled_date].push(j);
    });
    return m;
  }, [jobs]);

  const cells = [];
  const d = new Date(gridStart);
  while (d <= gridEnd) {
    cells.push(new Date(d));
    d.setDate(d.getDate() + 1);
  }

  const todayStr = fmt(new Date());

  return (
    <div className="space-y-8">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-sky-600 font-semibold mb-2">Pianificazione</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">Calendario</h1>
          <p className="text-slate-500 mt-2 text-sm">Visualizza tutti i traslochi del mese e naviga tra i mesi.</p>
        </div>
        <div className="flex items-center gap-2 bg-white rounded-xl border border-slate-200 p-1.5">
          <button
            onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
            className="p-2 rounded-lg hover:bg-slate-100"
            data-testid="calendar-month-prev-button"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="font-display font-bold text-slate-900 text-sm px-3 min-w-40 text-center" data-testid="calendar-current-month">
            {MONTHS[cursor.getMonth()]} {cursor.getFullYear()}
          </div>
          <button
            onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
            className="p-2 rounded-lg hover:bg-slate-100"
            data-testid="calendar-month-next-button"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </header>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="grid grid-cols-7 bg-slate-900 text-slate-300">
          {DAYS.map((d) => (
            <div key={d} className="px-3 py-3 text-[11px] uppercase tracking-widest font-bold text-center">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-px bg-slate-200">
          {cells.map((cell, i) => {
            const inMonth = cell >= monthStart && cell <= monthEnd;
            const dateStr = fmt(cell);
            const isToday = dateStr === todayStr;
            const dayJobs = byDate[dateStr] || [];
            return (
              <button
                key={i}
                onClick={() => setSelectedDay(dateStr)}
                data-testid={`calendar-day-${dateStr}`}
                className={`relative text-left bg-white min-h-[112px] p-2 transition hover:bg-sky-50/40 ${
                  !inMonth ? "opacity-40" : ""
                } ${isToday ? "ring-2 ring-inset ring-sky-500" : ""}`}
              >
                <div className={`font-mono text-xs font-bold ${isToday ? "text-sky-700" : "text-slate-500"}`}>
                  {cell.getDate()}
                </div>
                <div className="mt-1 space-y-1">
                  {dayJobs.slice(0, 3).map((j) => (
                    <div
                      key={j.id}
                      className={`truncate text-[11px] px-1.5 py-0.5 rounded font-semibold border ${
                        j.status === "completato"
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : j.status === "in_corso"
                          ? "bg-amber-50 text-amber-700 border-amber-200"
                          : j.status === "annullato"
                          ? "bg-rose-50 text-rose-700 border-rose-200"
                          : "bg-sky-50 text-sky-700 border-sky-200"
                      }`}
                    >
                      {j.start_time} {j.client_name}
                    </div>
                  ))}
                  {dayJobs.length > 3 && (
                    <div className="text-[10px] text-slate-400 font-mono">+{dayJobs.length - 3} altri</div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Day details dialog — vista grande del giorno */}
      <Dialog open={!!selectedDay} onOpenChange={(o) => !o && setSelectedDay(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="day-details-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl sm:text-3xl font-extrabold flex items-center gap-3 flex-wrap">
              {selectedDay && (() => {
                const d = new Date(selectedDay);
                return (
                  <>
                    <span className="inline-flex flex-col items-center bg-sky-600 text-white rounded-xl px-3 py-1 shadow-md shadow-sky-600/30">
                      <span className="text-[10px] uppercase tracking-widest font-bold opacity-90">{d.toLocaleDateString("it-IT", { weekday: "long" })}</span>
                      <span className="text-3xl font-extrabold leading-none">{d.getDate()}</span>
                      <span className="text-[10px] uppercase font-bold">{d.toLocaleDateString("it-IT", { month: "short" })} {d.getFullYear()}</span>
                    </span>
                    <span>Traslochi del Giorno</span>
                  </>
                );
              })()}
            </DialogTitle>
          </DialogHeader>
          {selectedDay && (
            <div className="space-y-3 pt-2">
              {(byDate[selectedDay] || []).length === 0 ? (
                <div className="bg-slate-50 rounded-xl border border-dashed border-slate-300 p-10 text-center text-slate-400">
                  <div className="text-5xl mb-2">🌴</div>
                  <div className="font-semibold">Nessun trasloco programmato in questa data</div>
                  <div className="text-xs mt-1">Giornata libera</div>
                </div>
              ) : (
                <>
                  <div className="text-xs text-slate-500 font-mono">
                    <b className="text-slate-900">{(byDate[selectedDay] || []).length}</b> trasloch{(byDate[selectedDay] || []).length === 1 ? "o" : "i"} in programma
                  </div>
                  <div className="grid gap-3 md:grid-cols-2">
                    {byDate[selectedDay].map((j) => (
                      <Link
                        key={j.id}
                        to={`/lavori/${j.id}`}
                        onClick={() => setSelectedDay(null)}
                        className="group bg-white rounded-xl border border-slate-200 p-4 hover:border-sky-400 hover:shadow-lg transition block"
                        data-testid={`day-detail-job-${j.id}`}
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="min-w-0">
                            <div className="font-display font-extrabold text-lg text-slate-900 truncate group-hover:text-sky-700">{j.client_name}</div>
                            <div className="text-xs font-mono text-slate-400 flex items-center gap-1 mt-0.5">
                              <Clock className="w-3 h-3" /> {j.start_time}{j.end_time && ` – ${j.end_time}`}
                              {j.depot_time && (
                                <span className="ml-1 inline-flex items-center gap-1 text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200 rounded px-1.5 py-0.5">
                                  🏭 {j.depot_time}
                                </span>
                              )}
                            </div>
                          </div>
                          <StatusBadge status={j.status} />
                        </div>
                        <div className="space-y-1 text-xs text-slate-600 mt-2">
                          <div className="flex items-start gap-1.5"><MapPin className="w-3 h-3 text-sky-500 shrink-0 mt-0.5" /><span className="truncate">{j.address_from}</span></div>
                          <div className="flex items-start gap-1.5"><MapPin className="w-3 h-3 text-emerald-500 shrink-0 mt-0.5" /><span className="truncate">{j.address_to}</span></div>
                        </div>
                        {(j.assigned_employees || []).length > 0 && (
                          <div className="mt-3 flex flex-wrap items-center gap-1">
                            <Users className="w-3 h-3 text-slate-400" />
                            {j.assigned_employees.map((e) => (
                              <span key={e.id} className={`text-[10px] font-semibold rounded px-1.5 py-0.5 ${j.foreman_id === e.id ? "bg-indigo-100 text-indigo-800 border border-indigo-200" : "bg-slate-100 text-slate-700"}`}>
                                {j.foreman_id === e.id && "👷 "}{e.name}
                              </span>
                            ))}
                          </div>
                        )}
                        <div className="mt-3 pt-2 border-t border-slate-100 flex items-center justify-between text-xs">
                          <span className="text-sky-600 font-semibold group-hover:text-sky-700">Apri dettagli</span>
                          <ArrowRight className="w-3.5 h-3.5 text-sky-600 group-hover:translate-x-1 transition" />
                        </div>
                      </Link>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
